import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff } from 'lucide-react';
import logo from '../assets/logo.png';


export default function AuthModal({ open, onClose, defaultTab = 'login' }) {
  const [tab, setTab] = useState(defaultTab);
  const [view, setView] = useState('auth'); // auth or forgot
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPw, setLoginPw] = useState('');
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPw, setRegPw] = useState('');

  const { login, register, loginWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();

  const reset = () => {
    setError(''); setLoginEmail(''); setLoginPw('');
    setRegName(''); setRegEmail(''); setRegPhone(''); setRegPw('');
    setView('auth');
  };

  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const u = await login(loginEmail, loginPw);
      onClose(); reset();
      if (u.role === 'admin') navigate('/admin'); else navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!loginEmail) { setError('Please enter your email first.'); return; }
    setLoading(true); setError('');
    try {
      await resetPassword(loginEmail);
      // We don't have showToast here, we use alert for now or just set a success message
      alert('Reset link sent to your email!');
      setView('auth');
    } catch (err) {
      setError(err.message || 'Failed to send reset link.');
    } finally { setLoading(false); }
  };

  const handleGoogleLogin = async () => {
    setLoading(true); setError('');
    try {
      await loginWithGoogle();
      // Supabase handles redirect
    } catch (err) {
      setError(err.message || 'Google login failed');
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (regPw.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true); setError('');
    try {
      await register(regName, regEmail, regPhone, regPw);
      onClose(); reset(); navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally { setLoading(false); }
  };

  const switchTab = (t) => { setTab(t); setError(''); };

  const inputClass = "w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-black text-sm placeholder:text-gray-400 focus:outline-none focus:border-black transition-colors";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); reset(); } }}>
      <DialogContent className="bg-white border border-gray-200 text-black max-w-md w-full p-0 overflow-hidden rounded-xl shadow-2xl">
        <DialogTitle className="sr-only">{tab === 'login' ? 'Login' : 'Register'}</DialogTitle>
        <DialogDescription className="sr-only">{tab === 'login' ? 'Sign in to your account' : 'Create a new account'}</DialogDescription>

        {/* Header */}
        <div className="px-8 pt-8 pb-4 text-center border-b border-gray-100">
          <div className="flex justify-center mb-4">
            <img src={logo} alt="The Barber Craft" className="h-14 w-auto object-contain" />
          </div>

          <h2 className="font-serif text-2xl text-black mt-2">
            {tab === 'login' ? 'Welcome Back' : 'Create Account'}
          </h2>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          <button data-testid="tab-login" onClick={() => switchTab('login')}
            className={`flex-1 py-3 text-sm font-semibold tracking-wide transition-colors ${tab === 'login' ? 'text-black border-b-2 border-black' : 'text-gray-400 hover:text-gray-600'}`}>
            Login
          </button>
          <button data-testid="tab-register" onClick={() => switchTab('register')}
            className={`flex-1 py-3 text-sm font-semibold tracking-wide transition-colors ${tab === 'register' ? 'text-black border-b-2 border-black' : 'text-gray-400 hover:text-gray-600'}`}>
            Register
          </button>
        </div>

        <div className="px-8 py-6">
          {error && (
            <div data-testid="auth-error" className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}

          {tab === 'login' ? (
            <div className="space-y-4">
              {view === 'auth' ? (
                <form onSubmit={handleLogin} data-testid="login-form" className="space-y-4">
                <div>
                  <label htmlFor="login-email" className="block text-xs text-gray-500 uppercase tracking-widest mb-1.5 font-semibold">Email</label>
                  <input id="login-email" name="email" autoComplete="email" data-testid="login-email" type="email" required value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
                    placeholder="your@email.com" className={inputClass} />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label htmlFor="login-password" name="password" className="block text-xs text-gray-500 uppercase tracking-widest font-semibold">Password</label>
                    <button type="button" onClick={() => setView('forgot')} className="text-[10px] text-black/40 hover:text-black transition-colors font-bold uppercase tracking-wider">
                      Forgot?
                    </button>
                  </div>
                  <div className="relative">
                    <input id="login-password" name="password" autoComplete="current-password" data-testid="login-password" type={showPw ? 'text' : 'password'} required value={loginPw} onChange={e => setLoginPw(e.target.value)}
                      placeholder="••••••••" className={`${inputClass} pr-10`} />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600">
                      {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                    </button>
                  </div>
                </div>
                <button data-testid="login-submit-btn" type="submit" disabled={loading}
                  className="w-full py-3 bg-black text-white font-semibold text-sm rounded-lg hover:bg-gray-800 transition-all active:scale-95 disabled:opacity-50 tracking-wide mt-2">
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <p className="text-xs text-gray-500 leading-relaxed mb-4">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <div>
                  <label htmlFor="reset-email" className="block text-xs text-gray-500 uppercase tracking-widest mb-1.5 font-semibold">Email</label>
                  <input id="reset-email" name="email" autoComplete="email" type="email" required value={loginEmail} onChange={e => setLoginEmail(e.target.value)}
                    placeholder="your@email.com" className={inputClass} />
                </div>
                <button type="submit" disabled={loading}
                  className="w-full py-3 bg-black text-white font-semibold text-sm rounded-lg hover:bg-gray-800 transition-all active:scale-95 disabled:opacity-50 tracking-wide mt-2">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button type="button" onClick={() => setView('auth')} className="w-full text-xs text-black/40 hover:text-black transition-colors font-bold uppercase tracking-widest mt-2">
                  Back to Login
                </button>
              </form>
            )}
              
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100"></div></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-gray-400 tracking-widest">Or continue with</span></div>
              </div>

              <button onClick={handleGoogleLogin} disabled={loading}
                className="w-full flex items-center justify-center gap-3 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-semibold">
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </button>
            </div>
          ) : (
            <form onSubmit={handleRegister} data-testid="register-form" className="space-y-4">
              <div>
                <label htmlFor="reg-name" className="block text-xs text-gray-500 uppercase tracking-widest mb-1.5 font-semibold">Full Name</label>
                <input id="reg-name" name="name" autoComplete="name" data-testid="reg-name" type="text" required value={regName} onChange={e => setRegName(e.target.value)}
                  placeholder="John Doe" className={inputClass} />
              </div>
              <div>
                <label htmlFor="reg-email" className="block text-xs text-gray-500 uppercase tracking-widest mb-1.5 font-semibold">Email</label>
                <input id="reg-email" name="email" autoComplete="email" data-testid="reg-email" type="email" required value={regEmail} onChange={e => setRegEmail(e.target.value)}
                  placeholder="your@email.com" className={inputClass} />
              </div>
              <div>
                <label htmlFor="reg-phone" className="block text-xs text-gray-500 uppercase tracking-widest mb-1.5 font-semibold">Phone</label>
                <input id="reg-phone" name="tel" autoComplete="tel" data-testid="reg-phone" type="tel" required value={regPhone} onChange={e => setRegPhone(e.target.value)}
                  placeholder="+91 99999 99999" className={inputClass} />
              </div>
              <div>
                <label htmlFor="reg-password" name="password" className="block text-xs text-gray-500 uppercase tracking-widest mb-1.5 font-semibold">Password</label>
                <div className="relative">
                  <input id="reg-password" name="password" autoComplete="new-password" data-testid="reg-password" type={showPw ? 'text' : 'password'} required value={regPw} onChange={e => setRegPw(e.target.value)}
                    placeholder="Min 6 characters" className={`${inputClass} pr-10`} />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600">
                    {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
              </div>
              <button data-testid="register-submit-btn" type="submit" disabled={loading}
                className="w-full py-3 bg-black text-white font-semibold text-sm rounded-lg hover:bg-gray-800 transition-all active:scale-95 disabled:opacity-50 tracking-wide mt-2">
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
